/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.19.0
 */

import { ICommonTranslation, IPageHistoryOrderTranslation } from '../../../modules/internationalization/types';
import {
    TableCell,
    TableCellDate,
    TableCellLink,
    TableCellStatus,
    TableCellText,
    TableFilterInputSearch,
    TableFilterRange,
    TableFilterRangeDatePicker,
    TableFilterSelect,
    TableHeader,
    TTableColumnConfig,
} from '../../../modules/table';
import { stringFormatParams } from '../../../utils/stringFormat';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { ITableOrder } from '../types';
import { getOrderStatusIcon } from '../utils/getOrderStatusIcon';
import TableOrderCellFile from './Cell/TableOrderCellFile';
import { useTableOrderCellAction } from './Cell/useTableOrderCellAction';

const useTableOrderColumns = (
    { table }: IPageHistoryOrderTranslation,
    { corporateTable }: ICommonTranslation,
): TTableColumnConfig<ITableOrder> => {

    const useActionCell = ({
                               action,
                               historyId,
                           }: ITableOrder) => useTableOrderCellAction(action.id, historyId, action.def);

    return {
        /* Действие */
        action: {
            accessor: 'action',
            Header: <TableHeader label={table.action.header} name="action">
                <TableFilterSelect name="action"/>
            </TableHeader>,
            ...TableCellLink<ITableOrder>(useActionCell),
        },
        /* Файлы для скачивания */
        files: {
            accessor: 'files',
            Header: '',
            Cell: (props) => TableCell(props)({
                render: ({ row }) => row.files?.map(file => (
                    <TableOrderCellFile file={file} key={row.id + file.id} row={row}/>
                )),
            }),
            disableResizing: true,
            width: 80,
            minWidth: 80,
        },
        /* Дата */
        date: {
            accessor: 'date',
            Header: <TableHeader label={table.date.header} name="date">
                <TableFilterRangeDatePicker name="date"/>
            </TableHeader>,
            ...TableCellDate<ITableOrder>(({ date }) => ({ date, format: EFormatData.FullData })),
            rowCount: 2,
        },
        /* Абоненты */
        subsCount: {
            accessor: 'subsCount',
            Header: <TableHeader label={table.subsCount.header} name="subsCount">
                <TableFilterRange name="subsCount"/>
            </TableHeader>,
            ...TableCellText<ITableOrder>(({ subsCount }) => subsCount),
            align: 'right',
        },
        /* Статус */
        status: {
            accessor: 'status',
            Header: <TableHeader label={table.status.header} name="status">
                <TableFilterSelect name="status"/>
            </TableHeader>,
            ...TableCellStatus<ITableOrder>(({ status }) => ({
                icon: getOrderStatusIcon(status.id),
                status: status.def,
            })),
        },
        /* Пользователь */
        user: {
            accessor: 'user',
            Header: <TableHeader label={table.user.header} name="user">
                <TableFilterInputSearch name="user"/>
            </TableHeader>,
            ...TableCellText<ITableOrder>(({ serviceUser, user }) => serviceUser
                ? stringFormatParams(corporateTable.serviceAccess.loginPrefix, { login: serviceUser })
                : user,
            ),
        },
    };
};

export default useTableOrderColumns;
